import React, { useState } from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import { useCityNameById } from "../../../hooks/useCity";
import DataTable from "../../shared/DataTable";
import { usePermission } from "../../../hooks/usePermission";
import { DeleteAttendeesPermission } from "../../../constants/permissions";
import {
  useDeletedAttendeeData,
  useDeleteFromDeletedAttendee,
  useRestoreDeletedAttendee,
} from "../../../hooks/useDeletedAttendee";

const CityNameCell = ({ cityId }) => {
  const { data: cityName } = useCityNameById(cityId);
  return cityName || "";
};

export const DeletedSubscribersTable = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const { data: deletedAttendees } = useDeletedAttendeeData();
  const { mutate: restoreAttendee } = useRestoreDeletedAttendee();
  const { mutate: deleteAttendee } = useDeleteFromDeletedAttendee();

  const { data: canDeleteAttendees } = usePermission({
    permissionName: DeleteAttendeesPermission,
  });

  const handleClick = (event, attendee) => {
    setAnchorEl(event.currentTarget);
    setSelectedAttendee(attendee);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedAttendee(null);
  };

  const ActionMenu = ({ row }) => (
    <>
      <IconButton onClick={(e) => handleClick(e, row)} size="small">
        <MoreVertIcon />
      </IconButton>
      {canDeleteAttendees && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem className="w-40">
            <button
              data-treack="restore-deleted-attendee-button"
              data-module="Attendees"
              onClick={() => {
                restoreAttendee(selectedAttendee.id);
                handleClose();
              }}
              className="text-blue-600"
            >
              Restore
            </button>
          </MenuItem>
          <MenuItem>
            <button
              data-treack="delete-deleted-attendee-button"
              data-module="Attendees"
              onClick={() => {
                deleteAttendee(selectedAttendee.id);
                handleClose();
              }}
              className="text-red-600"
            >
              Delete
            </button>
          </MenuItem>
        </Menu>
      )}
    </>
  );

  const columns = [
    {
      name: "National Id",
      selector: (row) => row.national_id,
      cell: (row) => <span className="text-blueColor">{row.national_id}</span>,
      width: "150px",
    },
    {
      name: "First Name",
      selector: (row) => row.first_name,
      cell: (row) => <span className="text-blueColor">{row.first_name}</span>,
      width: "150px",
    },
    {
      name: "Email",
      selector: (row) => row.email,
      cell: (row) => <span className="text-blueColor">{row.email}</span>,
      width: "200px",
    },
    {
      name: "Phone Number",
      selector: (row) => row.phone_number,
      cell: (row) => <span className="text-blueColor">{row.phone_number}</span>,
      width: "150px",
    },
    {
      name: "Specialty",
      selector: (row) => row.specialty,
      cell: (row) => <span className="text-blueColor">{row.specialty}</span>,
      width: "150px",
    },
    {
      name: "City",
      selector: (row) => {
        return <CityNameCell cityId={row.city_id} />;
      },
      cell: (row) => (
        <span className="text-blueColor">
          <CityNameCell cityId={row.city_id} />
        </span>
      ),
      width: "150px",
    },
    {
      name: "APO Number",
      selector: (row) => row.APO_number,
      cell: (row) => <span className="text-blueColor">{row.APO_number}</span>,
      width: "150px",
    },
    {
      name: "HCP",
      cell: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            row.APO_number
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {row.APO_number ? "Yes" : "No"}
        </span>
      ),
      width: "100px",
    },
    {
      name: "Signature",
      cell: (row) =>
        row.signature &&
        row.signature !== "No Signature" &&
        row.signature !== "" ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              window.open(row.signature, "_blank");
            }}
            className="text-blueColor hover:text-blue-700 font-medium underline"
          >
            View Signature
          </button>
        ) : (
          <span className="text-blueColor">No Signature</span>
        ),
      width: "150px",
    },
    {
      name: "Actions",
      cell: (row) => <ActionMenu row={row} />,
      width: "100px",
    },
  ];

  return (
    <div className="container my-5">
      <div className="bg-white shadow-md my-4 w-1/2 mx-auto">
        <div className="container mx-auto">
          <nav className="flex justify-center space-x-8">
            <button
              className={`px-6 py-4 text-lg font-medium relative hover:bg-gray-50 transition-all duration-200
                                ${
                                  window.location.pathname === "/deleted-events"
                                    ? "text-purpleColor"
                                    : "text-gray-500 hover:text-purpleColor"
                                }`}
              onClick={() => navigate("/deleted-events")}
            >
              <div className="flex items-center gap-2">
                <i className="fas fa-trash-restore"></i>
                <span>Deleted Events</span>
              </div>
              <div
                className={`absolute bottom-0 left-0 w-full h-0.5 ${
                  window.location.pathname === "/deleted-events"
                    ? "bg-purpleColor"
                    : "bg-transparent"
                }`}
              ></div>
            </button>
            <button
              className={`px-6 py-4 text-lg font-medium relative hover:bg-gray-50 transition-all duration-200
                                ${
                                  window.location.pathname ===
                                  "/deleted-subscribers"
                                    ? "text-purpleColor border-b-2 border-purpleColor"
                                    : "text-gray-500 hover:text-purpleColor"
                                }`}
              onClick={() => navigate("/deleted-subscribers")}
            >
              <div className="flex items-center gap-2">
                <i className="fas fa-users-slash"></i>
                <span>Deleted Attendees</span>
              </div>
              <div
                className={`absolute bottom-0 left-0 w-full h-0.5 ${
                  window.location.pathname === "/deleted-subscribers"
                    ? "bg-purpleColor"
                    : "bg-transparent"
                }`}
              ></div>
            </button>
          </nav>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={deletedAttendees || []}
        searchFields={[
          "national_id",
          "first_name",
          "email",
          "phone_number",
          "specialty",
        ]}
        searchPlaceholder="Search attendees..."
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 30]}
      />
    </div>
  );
};
